import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "../../Components/ui/card";
import { Button } from "../../Components/ui/button.jsx";
import {
  Dollar,
  info,
  PaperAirplaneIcon,
  refresh,
} from "../../Public/assets/index";
import { Input } from "../../Components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../Components/ui/select";
import { useOverview } from "./../../hooks";
import { TransferApi } from "./../../api";
import { useDebounce } from "./../../Utils/helperFunction";
import Loader from "./../../Components/Loader/PageLoader";
import InfoTag from "./../../Components/SendMoney/InfoTag";
import { TAG_INFO, TRANSFER_METHODS } from "./../../Utils/Contant";

const TransferDetails = ({
  changeMode,
  setChangeMode,
  beneficiarySelected,
}) => {
  // State for tracking which input field (FROM/TO) is active
  const [conversionInputActivity, setConversionInputActivity] = useState(null);

  // Get overview hook data
  const {
    getFlagByCurrencyCode,
    amount,
    setAmount,
    toAmount,
    setToAmount,
    isLoading,
    setIsLoading,
    setBeneficiarySelected,
    getFlagByCurrencyCodeForBeni,
    charges,
    setCharges,
    selectedCurrency,
    setSelectedCurrency,
    selectedToCurrency,
    setSelectedToCurrency,
    selectedMethodToTransfer,
    setSelectedMethodToTransfer,
    useWalletDetails,
    fromAmountError,
    toAmountError,
    setFromAmountError,
    setToAmountError,
    investmentAccount,
    validateAmount,
    setSelectedDuration,
    setCurrentFlow,
    setStateVariable,
    useBeneficiaryDetails,
    isHedgeFundAccount,
  } = useOverview();

  // Fetch wallet and beneficiary data
  const { data: walletData } = useWalletDetails();
  // All statuses: MANUAL_REVIEW siblings must be visible to block their method
  const { data: beneficiaryData } = useBeneficiaryDetails({
    status: undefined,
  });

  useEffect(() => {
    if (walletData?.data?.accounts?.length > 0) {
      const firstAccount = walletData.data.accounts[0];
      setSelectedCurrency(firstAccount);
    }
  }, [walletData]);

  // Initialize method from beneficiary when component mounts
  useEffect(() => {
  if (beneficiarySelected?.beneficiaryId) {
    setSelectedMethodToTransfer(beneficiarySelected?.method);
    if (beneficiarySelected?.currency) {
      const matchingAccount = walletData?.data?.accounts?.find(
        (account) => account.vendorCurrency === beneficiarySelected.currency
      );
      setSelectedToCurrency(matchingAccount || { vendorCurrency: beneficiarySelected.currency }
      );
      setConversionInputActivity("FROM");
    }
  }
}, [beneficiarySelected]);

  // Update selected duration when method changes
  useEffect(() => {
    if (selectedMethodToTransfer) {
      const method = TRANSFER_METHODS[selectedMethodToTransfer];
      if (method) {
        setSelectedDuration(method.duration);
      }
    }
  }, [selectedMethodToTransfer]);

  // Update the useEffect that initializes currencies and conversion data
  useEffect(() => {
    const fetchInitialTransferData = async () => {
      // Only proceed if we have accounts data
      if (!walletData?.data?.accounts?.length) return;

      // Filter accounts to match beneficiary currency if applicable
      const filteredAccountsDetails = beneficiarySelected?.currency
        ? walletData?.data?.accounts?.filter(
            (account) =>
              account?.vendorCurrency === selectedCurrency ||
              (account?.vendorCurrency === beneficiarySelected?.currency &&
                account?.balance > 0)
          )
        : [];

      if (!selectedCurrency?.vendorCurrency) {
        // If we have filtered accounts matching beneficiary currency with balance, use the first one
        // Otherwise fall back to the first account from walletData
        setSelectedCurrency(
          filteredAccountsDetails?.length > 0
            ? filteredAccountsDetails[0]
            : walletData?.data?.accounts?.[0]
        );
      }

      if (!selectedToCurrency?.vendorCurrency) {
        // Case 1: Investment Account - always use USD
        if (investmentAccount) {
          const usdAccount = walletData?.data?.accounts?.find(
            (account) => account.vendorCurrency === "USD"
          );
          setSelectedToCurrency(usdAccount || { vendorCurrency: "USD" });
        }
        // Case 2: Local transfer method with beneficiary selected - use beneficiary currency
        else if (
          (selectedMethodToTransfer === "LOCAL" || !selectedMethodToTransfer) &&
          beneficiarySelected?.currency
        ) {
          const matchingAccount = walletData?.data?.accounts?.find(
            (account) => account.vendorCurrency === beneficiarySelected.currency
          );
          setSelectedToCurrency(
            matchingAccount || {
              vendorCurrency: beneficiarySelected.currency,
            }
          );
        }
        // Case 3: Default - use first account
        else {
          setSelectedToCurrency(walletData?.data?.accounts?.[0]);
        }
      }
      if (amount && toAmount) return;

      try {
        if (
          selectedCurrency?.vendorCurrency &&
          (selectedToCurrency?.vendorCurrency ||
            (typeof selectedToCurrency === "string" && selectedToCurrency))
        ) {
          const toCurrencyValue =
            typeof selectedToCurrency === "object"
              ? selectedToCurrency.vendorCurrency
              : selectedToCurrency;

          const conversionCheck = await TransferApi({
            sellAmount: 1,
            fromCurrency: selectedCurrency?.vendorCurrency,
            toCurrency: toCurrencyValue,
            transactionType: "TRANSFER",
            selectedMethodToTransfer: selectedMethodToTransfer || "LOCAL",
          });

          setCharges({
            totalFees: amount ? conversionCheck?.data?.totalFees : "-",
            rate: conversionCheck?.data?.rate,
          });
        }
      } catch (err) {
        console.error("Error fetching conversion data:", err);
      }
    };

    fetchInitialTransferData();
  }, [
    walletData,
    selectedMethodToTransfer,
    selectedToCurrency,
    selectedCurrency,
    // beneficiarySelected,
    // investmentAccount,
  ]);
  // Format currency list for dropdown
  const currencies =
    walletData?.data?.accounts?.map((account) => ({
      flag: getFlagByCurrencyCode(account.vendorCurrency),
      balance: account.balance,
      vendorCurrency: account.vendorCurrency,
      isFaded: !(parseFloat(account.balance) - parseFloat(account.holdBalance) > 0),
      ...account,
    })) || [];

  // Debounced API call for conversion calculations
  const debouncedFetchWalletTransfer = useCallback(
    useDebounce(async (payload) => {
      try {
        if ((amount || toAmount) && changeMode === false) {
          setToAmountError("");
          setIsLoading(true);
          const transfer = await TransferApi(payload);

          setCharges({
            totalFees: transfer?.data?.totalFees,
            rate: transfer?.data?.rate,
          });

          setToAmount(transfer?.data?.toAmount);
          setAmount(transfer?.data?.fromAmount);

          const amountError = validateAmount(
            transfer?.data?.fromAmount,
            (
              selectedCurrency?.balance - selectedCurrency?.holdBalance
            )?.toFixed(2),
            transfer?.data?.totalFees
          );

          setFromAmountError(amountError);
          setConversionInputActivity(null);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Transfer calculation error:", err);
        setIsLoading(false);
      }
    }, 1500),
    [amount, toAmount, conversionInputActivity, selectedCurrency]
  );

  // Helper Functions
  const getAvailableBalance = () => {
    if (!selectedCurrency) return "";
    return parseFloat(
      selectedCurrency?.balance - selectedCurrency?.holdBalance
    ).toFixed(2);
  };
  const availableAmountDisplay = getAvailableBalance();

  const getFeeDisplay = () => {
    if (!charges?.totalFees || charges.totalFees === "-") {
      return "-";
    }

    const isZero = parseFloat(charges.totalFees) === 0;
    // selectedCurrency?.vendorCurrency === "USD" ||
    if (isZero) {
      return "Free";
    }

    return `${charges.totalFees} ${selectedCurrency?.vendorCurrency}`;
  };

  // Status of the sibling record (same payee, given method):
  // "APPROVED" wins, then "MANUAL_REVIEW"; anything else counts as not existing.
  const getSiblingByMethod = (beneficiaries, targetBeneficiary, method) => {
    const siblings = (beneficiaries || []).filter(
      (b) =>
        b.maskAccountNo === targetBeneficiary?.maskAccountNo &&
        b.country === targetBeneficiary?.country &&
        b.method === method
    );
    const statusOf = (b) => b?.status?.trim()?.toUpperCase();
    return (
      siblings.find((b) => statusOf(b) === "APPROVED") ||
      siblings.find((b) => statusOf(b) === "MANUAL_REVIEW")
    );
  };

  const isMethodUnderReview = (method) => {
    const sibling = getSiblingByMethod(
      beneficiaryData?.data?.beneficiaries,
      beneficiarySelected,
      method
    );
    return sibling?.status?.trim()?.toUpperCase() === "MANUAL_REVIEW";
  };

  const validateMethodChange = (beneficiaries, beneficiaryId, newMethod) => {
    const targetBeneficiary = beneficiaries?.find(
      (b) => b.beneficiaryId === beneficiaryId
    );

    if (!targetBeneficiary) return { isValid: false };

    const sibling = getSiblingByMethod(
      beneficiaries,
      targetBeneficiary,
      newMethod
    );
    const status = sibling?.status?.trim()?.toUpperCase();

    return {
      isValid: status === "APPROVED",
      isUnderReview: status === "MANUAL_REVIEW",
      beneficiaryDetails: sibling,
    };
  };

  const handleMethodSelection = (method, data) => {
    if (changeMode && isMethodUnderReview(method)) {
      // Method exists but is still under review: not selectable
      return;
    }
    if (changeMode) {
      const beniCheck = validateMethodChange(
        beneficiaryData?.data?.beneficiaries,
        beneficiarySelected?.beneficiaryId,
        method
      );

      setSelectedDuration(data?.duration);

      if (beniCheck.isValid) {
        setConversionInputActivity("FROM");
        setSelectedMethodToTransfer(method);
        setBeneficiarySelected(beniCheck?.beneficiaryDetails);

        // Update TO currency based on method and beneficiary
        if (method === "LOCAL" && beniCheck?.beneficiaryDetails?.currency) {
          // For LOCAL method, use beneficiary currency
          const newToCurrency = {
            vendorCurrency: beniCheck?.beneficiaryDetails?.currency,
          };
          setSelectedToCurrency(newToCurrency);
        }
      } else {
        setCurrentFlow("ChangeMethod");
        setStateVariable(1);
      }
      setChangeMode(false);
    } else {
      setChangeMode(true);
    }
  };

  const handleCurrencyChange = (value) => {
    const selected = currencies.find(
      (currency) => currency.vendorCurrency === value
    );
    setSelectedCurrency(selected);
    setFromAmountError("");
    setToAmountError("");
    setConversionInputActivity("FROM");
  };

  const handleToCurrencyChange = (value) => {
    const selected = currencies.find(
      (currency) => currency?.vendorCurrency === value
    );

    setSelectedToCurrency(selected);
    setFromAmountError("");
    setToAmountError("");
    setConversionInputActivity("FROM");
  };

  const handleAmountChange = (e, isFromAmount = true) => {
    const inputValue = e.target.value;

    if (isFromAmount) {
      setConversionInputActivity("FROM");
      const amountError = validateAmount(
        inputValue,
        getAvailableBalance(),
        charges?.totalFees
      );
      setFromAmountError(amountError);
      if (inputValue === "") {
        setAmount("");
        setToAmount("");
      } else {
        setAmount(inputValue);
      }
    } else {
      setConversionInputActivity("TO");
      // const amountError = validateAmount(
      //   inputValue,
      //   getAvailableBalance(),
      //   availableAmountDisplay,

      //   charges?.totalFees
      // );
      // setFromAmountError(amountError);
      if (inputValue === "") {
        setAmount("");
        setToAmount("");
      } else {
        setToAmount(inputValue);
      }
    }
  };

  useEffect(() => {
    // Don't proceed if we're missing essential data
    if (!selectedCurrency?.vendorCurrency) return;

    // Ensure we have a valid selectedToCurrency, falling back to wallet data if needed
    if (!selectedToCurrency && walletData?.data?.accounts?.length > 0) {
      // Case 1: Investment Account - always use USD
      if (investmentAccount) {
        const usdAccount = walletData?.data?.accounts?.find(
          (account) => account.vendorCurrency === "USD"
        );
        setSelectedToCurrency(usdAccount || { vendorCurrency: "USD" });
        return;
      }
      // Case 2: Local transfer with beneficiary
      else if (
        selectedMethodToTransfer === "LOCAL" &&
        beneficiarySelected?.currency
      ) {
        setSelectedToCurrency({ vendorCurrency: beneficiarySelected.currency });
        return;
      }
      // Case 3: Default fallback
      else {
        setSelectedToCurrency(walletData?.data?.accounts?.[0]);
        return;
      }
    }

    // Skip if there's an error or no input yet
    // fromAmountError ||
    if (!amount && !toAmount && !conversionInputActivity) return;

    // Determine the correct toCurrency value based on context
    let toCurrencyValue;

    // Case 1: Investment account - always use USD
    if (investmentAccount) {
      toCurrencyValue = "USD";
    }
    // Case 2: LOCAL method with beneficiary - use beneficiary currency
    else if (
      selectedMethodToTransfer === "LOCAL" &&
      beneficiarySelected?.currency
    ) {
      toCurrencyValue = beneficiarySelected.currency;
    }
    // Case 3: selectedToCurrency is an object - use its vendorCurrency
    else if (
      typeof selectedToCurrency === "object" &&
      selectedToCurrency?.vendorCurrency
    ) {
      toCurrencyValue = selectedToCurrency.vendorCurrency;
    }
    // Case 4: selectedToCurrency is already a string
    else if (typeof selectedToCurrency === "string") {
      toCurrencyValue = selectedToCurrency;
    }
    // Case 5: Fallback to first wallet account
    else if (walletData?.data?.accounts?.length > 0) {
      toCurrencyValue = walletData.data.accounts[0].vendorCurrency;
    }

    const basePayload = {
      fromCurrency: selectedCurrency?.vendorCurrency,
      toCurrency: toCurrencyValue,
      transactionType: "TRANSFER",
      selectedMethodToTransfer: selectedMethodToTransfer || "LOCAL",
      country: beneficiarySelected?.country,
    };

    if (conversionInputActivity === "FROM" && amount) {
      debouncedFetchWalletTransfer({
        ...basePayload,
        sellAmount: amount,
      });
    } else if (conversionInputActivity === "TO" && toAmount) {
      debouncedFetchWalletTransfer({
        ...basePayload,
        buyAmount: toAmount,
      });
    } else if (conversionInputActivity && !amount && !toAmount) {
      // Force a conversion with default value of 1 if currency changed but no amount entered
      debouncedFetchWalletTransfer({
        ...basePayload,
        sellAmount: 1,
      });
    }
  }, [
    selectedCurrency,
    selectedToCurrency,
    amount,
    toAmount,
    selectedMethodToTransfer,
    beneficiarySelected,
    investmentAccount,
    changeMode,
    fromAmountError,
    conversionInputActivity,
    walletData,
  ]);
  // Component to render transfer method card
  const MethodCard = ({ method, data }) => {
    const underReview = changeMode && isMethodUnderReview(method);
    return (
      <Card
        className={`border border-[#D3D4DA] ml-10 mt-2 ${
          selectedMethodToTransfer === method && !changeMode
            ? "bg-orangeShade"
            : ""
        } ${underReview ? "bg-greyShade cursor-not-allowed" : ""}`}
        onClick={() => handleMethodSelection(method, data)}
      >
        <CardContent className="p-4">
          <div className={`flex justify-between items-center ${underReview ? "opacity-60" : ""}`}>
            <div className="space-y-2">
              <p className="font-geomanistBold text-sm text-blackShade">
                {data.title}
              </p>
              <p className="font-normal text-xs text-blackShade">
                ({data.duration})
              </p>
              {changeMode && (
                <p className="font-normal text-xs text-blackShade">
                  Charges: {data.charges}
                </p>
              )}
            </div>
            {underReview ? (
              <div onClick={(e) => e.stopPropagation()} className="opacity-100">
                <InfoTag label="Under review" info={TAG_INFO.MANUAL_REVIEW} />
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className={`text-blackShade border-none font-geomanistBold text-xs ${
                  changeMode ? "!bg-[#D0EBFF] " : ""
                }`}
              >
                {changeMode ? "Select" : "Change"}
              </Button>
            )}
          </div>
          {data.recommended && !underReview && (
            <div className="absolute top-4 right-1">
              <span className="rounded-full font-geomanistBold text-[12px] py-[5px] px-[10px] bg-blueish text-white">
                Recommended
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="py-[19px] space-y-[19px]">
      {/* Beneficiary information card */}
      <BeneficiaryCard
        beneficiary={beneficiarySelected}
        isInvestmentAccount={investmentAccount}
        isHedgeFundAccount={isHedgeFundAccount}
      />

      {/* Source amount input */}
      <SourceAmountSection
        amount={amount}
        selectedCurrency={selectedCurrency}
        currencies={currencies}
        fromAmountError={fromAmountError}
        availableBalance={availableAmountDisplay}
        onAmountChange={handleAmountChange}
        onCurrencyChange={handleCurrencyChange}
        loading={isLoading}
      />

      {/* Transfer method and conversion section */}
      <TransferMethodSection
        changeMode={changeMode}
        selectedMethodToTransfer={selectedMethodToTransfer}
        selectedCurrency={selectedCurrency}
        isLoading={isLoading}
        charges={charges}
        selectedToCurrency={selectedToCurrency}
        getFeeDisplay={getFeeDisplay}
        MethodCard={MethodCard}
      />

      {/* Destination amount section */}
      <DestinationAmountSection
        toAmount={toAmount}
        toAmountError={toAmountError}
        onAmountChange={handleAmountChange}
        currencies={currencies}
        selectedMethodToTransfer={selectedMethodToTransfer}
        selectedToCurrency={selectedToCurrency}
        beneficiarySelected={beneficiarySelected}
        onCurrencyChange={handleToCurrencyChange}
        getFlagByCurrencyCodeForBeni={getFlagByCurrencyCodeForBeni}
        investmentAccount={investmentAccount}
        loading={isLoading}
      />
    </div>
  );
};

// Extracted Components
const BeneficiaryCard = ({
  beneficiary,
  isInvestmentAccount,
  isHedgeFundAccount,
}) => (
  <Card className="shadow-send-money-gradient bg-[#F7F7F8] border-[1px] border-[#D3D4DA]">
    <CardContent className="relative p-4 gap-4 flex items-center">
      <div className="space-y-1">
        <p
          className="font-geomanistBold text-blackShade text-sm max-w-[200px] truncate"
          title={beneficiary?.nameOfBeni}
        >
          {beneficiary?.nameOfBeni}
        </p>
        <p className="text-xs font-geomanistBold text-blackShade">
          {beneficiary?.maskAccountNo || beneficiary?.iban}
          <span className="pl-[6px] text-greyish">{beneficiary?.bankName}</span>
        </p>
      </div>
      <div className="flex justify-end">
        {isInvestmentAccount && (
          <span className="absolute top-6 right-2 rounded-full font-geomanistBold text-[12px] py-[5px] px-[10px] bg-blueish text-white">
            Investment Account
          </span>
        )}
        {isHedgeFundAccount && (
          <span className="absolute top-6 right-2 rounded-full font-geomanistBold text-[12px] py-[5px] px-[10px] bg-[#FFE2C7] text-[#010101]">
            Hedge Fund Account
          </span>
        )}
      </div>
    </CardContent>
  </Card>
);

const SourceAmountSection = ({
  amount,
  selectedCurrency,
  currencies,
  fromAmountError,
  availableBalance,
  onAmountChange,
  onCurrencyChange,
  loading,
}) => (
  <div className="space-y-2">
    <p className="text-xs font-normal text-blackShade">You Pay</p>
    <div className="space-y-1">
      <div className="flex items-center">
        <Input
          type="number"
          value={amount}
          onChange={(e) => onAmountChange(e, true)}
          className={`text-sm border-r-0 font-geomanistBold rounded-r-none ${
            fromAmountError ? "border-red-500" : ""
          }`}
          disabled={loading}
        />
        <Select
          value={selectedCurrency?.vendorCurrency || ""}
          onValueChange={onCurrencyChange}
        >
          <SelectTrigger
            className={`w-[110px] font-geomanistBold border-l-0 rounded-l-none bg-greyShade ${
              fromAmountError ? "border-red-500 border-l-0" : ""
            }`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[9999]">
            {currencies?.map((currency, index) => (
              <SelectItem
                isLastItem={index === currencies.length - 1}
                key={currency.vendorCurrency}
                value={currency.vendorCurrency}
                className={
                  currency.isFaded
                    ? "opacity-50 transition-opacity duration-500 pointer-events-none cursor-not-allowed"
                    : ""
                }
              >
                <div className="flex items-center">
                  <span className="mr-2">
                    <img src={currency.flag} alt="" />
                  </span>
                  <span className="font-geomanistMedium">
                    {currency.vendorCurrency}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {fromAmountError && (
        <p className="text-red-500 text-xs mt-1">{fromAmountError}</p>
      )}
      <p className="text-[12px] space-x-2 font-normal text-greyish flex justify-end">
        Available Amount: {availableBalance} {selectedCurrency?.vendorCurrency}
      </p>
    </div>
  </div>
);

const TransferMethodSection = ({
  changeMode,
  selectedMethodToTransfer,
  selectedCurrency,
  selectedToCurrency,
  isLoading,
  charges,
  getFeeDisplay,
  MethodCard,
}) => (
  <div className="flex flex-col gap-5 relative">
    <div className="absolute left-4 top-0 bottom-0 border-l-2 border-dotted border-greyish"></div>

    {/* Transfer method section */}
    <div>
      <div className="absolute left-2.5 top-0 ">
        <div className="transform rotate-25 bg-white text-greyish">
          <PaperAirplaneIcon />
        </div>
      </div>
      <p className="text-sm font-normal leading-[20px] pl-10 text-greyish">
        {changeMode ? "Choose Transfer Method" : "Transfer Method"}
      </p>

      {/* Show all methods if in change mode, otherwise just show selected */}
      {changeMode ? (
        Object.entries(TRANSFER_METHODS).map(([method, data]) => (
          <MethodCard key={method} method={method} data={data} />
        ))
      ) : (
        <MethodCard
          method={selectedMethodToTransfer}
          data={TRANSFER_METHODS[selectedMethodToTransfer]}
        />
      )}
    </div>

    {/* Conversion rate section - only for non-USD currencies */}
    {!(
      selectedCurrency?.vendorCurrency === selectedToCurrency?.vendorCurrency
    ) && (
      <div>
        <div className="absolute left-2.5 bg-white ">
          <div className="text-blue-500 flex pt-1 items-center transform rotate-45">
            <img src={refresh} alt="" />
          </div>
        </div>

        <div className="space-y-2 pl-12">
          <div className="flex items-center">
            <p className="text-xs flex items-center gap-2 font-normal text-[#797979]">
              Conversion Rate
              <div className="flex gap-1 h-6 items-center text-base font-geomanistBold text-[#22212E]">
                <span className="flex items-center text-base font-geomanistBold text-[#22212E]">
                  1 {selectedCurrency?.vendorCurrency} =
                </span>
                {isLoading ? <Loader className="w-6 h-6" /> : charges?.rate}{" "}
                {typeof selectedToCurrency === "object"
                  ? selectedToCurrency?.vendorCurrency
                  : selectedToCurrency}
              </div>
              <div className="relative group">
                <img src={info} alt="" />
                <div className="absolute left-1/2 -translate-x-1/2 hidden group-hover:block bottom-full w-max mb-2 bg-blueish font-geomanistBold text-white text-xs leading-[24px] rounded px-2 py-1">
                  Actual rate may vary
                  <div className="absolute bottom--2 left-1/2 -translate-x-1/2 w-2 h-2 bg-blueish rotate-45 transform"></div>
                </div>
              </div>
            </p>
          </div>
        </div>
      </div>
    )}

    {/* Transfer fee section */}
    <div>
      <div className="absolute left-1.5 bottom-0 bg-white">
        <img src={Dollar} alt="" />
      </div>
      <div className="space-y-2 pl-12">
        <div className="flex items-center">
          <p className="text-xs flex items-center gap-2 font-normal text-[#797979]">
            Transfer Fee:
            {!changeMode ? (
              <span className="flex gap-1 text-base font-geomanistBold text-[#22212E]">
                {isLoading ? <Loader className="w-6 h-6" /> : getFeeDisplay()}
              </span>
            ) : (
              "  - "
            )}
            {getFeeDisplay() !== "FREE" && (
              <div className="relative group">
                <img src={info} alt="" />
                <div className="absolute left-1/2 -translate-x-1/2 hidden group-hover:block bottom-full w-max mb-2 bg-blueish font-geomanistBold text-white text-xs leading-[24px] rounded px-2 py-1">
                  Actual fee may vary depending on the rate
                  <div className="absolute bottom--2 left-1/2 -translate-x-1/2 w-2 h-2 bg-blueish rotate-45 transform"></div>
                </div>
              </div>
            )}
          </p>
        </div>
      </div>
    </div>
  </div>
);

const DestinationAmountSection = ({
  toAmount,
  toAmountError,
  onAmountChange,
  currencies,
  selectedMethodToTransfer,
  selectedToCurrency,
  beneficiarySelected,
  getFlagByCurrencyCodeForBeni,
  onCurrencyChange,
  investmentAccount,
  loading,
}) => {
  return (
    <div className="space-y-2">
      <p className="text-xs font-normal text-blackShade">Payee Gets</p>
      <div className="flex items-center">
        <Input
          type="number"
          value={toAmount}
          onChange={(e) => onAmountChange(e, false)}
          className={`text-sm border-r-0 font-geomanistBold rounded-r-none ${
            toAmountError ? "border-red-500" : ""
          }`}
          disabled={loading}
        />

        {selectedMethodToTransfer === "LOCAL" || investmentAccount ? (
          <div
            className={`w-[110px] gap-2 h-10 border flex items-center justify-center rounded border-l-0 rounded-l-none bg-[#f7f7f8]
         ${toAmountError ? "border-red-500 border-l-0" : ""}`}
          >
            <div className="flex items-center">
              <span className="mr-2">
                <img
                  src={
                    investmentAccount
                      ? getFlagByCurrencyCodeForBeni("USD")
                      : getFlagByCurrencyCodeForBeni(
                          // beneficiarySelected?.currency
                          beneficiarySelected.country
                        )
                  }
                  alt={
                    investmentAccount ? "USD" : beneficiarySelected?.currency
                  }
                  height={18}
                  width={18}
                />
              </span>
              <span className="font-geomanistMedium">
                {investmentAccount ? "USD" : beneficiarySelected?.currency}
              </span>
            </div>
          </div>
        ) : (
          <Select
            value={selectedToCurrency?.vendorCurrency || ""}
            onValueChange={onCurrencyChange}
          >
            <SelectTrigger
              className={`w-[110px] font-geomanistBold border-l-0 rounded-l-none bg-greyShade ${
                toAmountError ? "border-red-500 border-l-0" : ""
              }`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[9999]">
              {currencies?.map((currency, index) => (
                <SelectItem
                  isLastItem={index === currencies.length - 1}
                  key={currency.vendorCurrency}
                  value={currency.vendorCurrency}
                  // className={
                  //   // currency.isFaded
                  //     ? "opacity-50 transition-opacity duration-500 pointer-events-none cursor-not-allowed"
                  //     : ""
                  // }
                >
                  <div className="flex items-center">
                    <span className="mr-2">
                      <img src={currency.flag} alt="" />
                    </span>
                    <span className="font-geomanistMedium">
                      {currency.vendorCurrency}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {toAmountError && (
        <p className="text-red-500 text-xs mt-1">{toAmountError}</p>
      )}
    </div>
  );
};

export default TransferDetails;
